<template>
    <div class="runner-list-grid">
        <table v-if="!$store.state.isMobile" class="runner-table">
            <thead>
                <tr>
                    <th :colspan="3+games.length" style="padding-left: 15px; padding-right: 15px">
                        <input type="text" class="input" placeholder="Search" v-model="search" @keydown="applySearch()" />
                    </th>
                </tr>
                <tr>
                    <th class="ranks" width="1">Rank</th>
                    <th>Runner</th>
                    <th>Points</th>
                    <th v-for="game in games" :key="game.Id" @click="filter(game)" class="game-header">
                        <span class="filter-button" :class="{filterRequire: gameFilter == game.Id}">{{game.UrlStub}}</span>
                    </th>
                </tr>
            </thead>
            <tbody v-if="!runnerList">
                <tr v-for="i in 100" :key="i">
                    <td></td>
                    <td>
                        <ContentLoader width="170" height="18" primary-color="#1f2a3c" secondary-color="#2c3b55">
                            <rect x="0" y="5" rx="2" ry="2" :width="(150 + Math.random() * 20)" height="15" />
                        </ContentLoader>
                    </td>
                    <td></td>
                    <td></td>
                </tr>
            </tbody>
            <tbody v-if="runnerList">
                <tr v-for="user in runnerList" :key="user.rank">
                    <td class="ranks"><span class="rank">{{ user.rank }}</span></td>
                    <td>
                        <country-flag v-if="user.CountryDesignation" :country="user.CountryDesignation" size="small" :title="`${user.CountryDesignation} flag`" />&nbsp;
                        <router-link :to="'/profiles/' + user.Username">{{user.Username}}</router-link>
                        <a target="_blank"  rel="noopener noreferrer" :href="'https://twitch.tv/' + user.TwitchAccount" v-if="user.TwitchAccount" class="social-link twitch"><img src="/img/TwitchGlitchWhite.svg" /></a>
                        <a target="_blank"  rel="noopener noreferrer" :href="user.YoutubeUrl" v-if="user.YoutubeUrl" class="social-link youtube"><img src="/img/YoutubeWhite.svg" /></a>
                    </td>
                    <td>{{user.Points}}</td>
                    <td v-for="game in games" :key="game.Id" class="game-symbol">
                        <span v-if="gameFilter && gameFilter == game.Id">{{ user.PointsPerGame![game.Id] }}</span>
                        <i v-else-if="user.SubmittedGames.indexOf(game.Id) >= 0" class="fas fa-check"></i>
                        <i v-else class="fas fa-minus darker"></i>
                    </td>
                </tr>
                <tr v-if="!loadedFull">
                    <td colspan="999" class="has-text-centered">
                        <button class="button is-primary" @click="loadFullList()">Load More Runners</button>
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-else class="runner-cards">
            <template v-if="!runnerList">
                <div v-for="i in 100" :key="i" class="runner-card">
                    <ContentLoader width="250" height="75" primary-color="#1f2a3c" secondary-color="#2c3b55">
                        <rect x="25" y="5" rx="2" ry="2" :width="(150 + Math.random() * 50)" height="15" />
                        <rect x="25" y="30" rx="2" ry="2" :width="(150 + Math.random() * 50)" height="15" />
                    </ContentLoader>
                </div>
            </template>
            <template v-if="runnerList">
                <div v-for="user in runnerList" :key="user.rank" class="runner-card">
                    <div class="runner-info">
                        <div class="headline">
                            <div class="rank">{{user.rank}}</div>
                            <div>
                                <country-flag v-if="user.CountryDesignation" :country="user.CountryDesignation" size="small" :title="`${user.CountryDesignation} flag`" />&nbsp;
                                <router-link :to="'/profiles/' + user.Username">{{user.Username}}</router-link>
                                <div>{{user.Points}} points</div>
                            </div>
                        </div>
                        <div>
                            <a target="_blank"  rel="noopener noreferrer" :href="'https://twitch.tv/' + user.TwitchAccount" v-if="user.TwitchAccount" class="social-link twitch"><img src="/img/TwitchGlitchWhite.svg" /></a>
                            <a target="_blank"  rel="noopener noreferrer" :href="user.YoutubeUrl" v-if="user.YoutubeUrl" class="social-link youtube"><img src="/img/YoutubeWhite.svg" /></a>
                        </div>
                    </div>
                    <div class="game-symbols">
                        <span v-for="game in games" :key="game.Id" class="game-symbol" :class="{'submitted': user.SubmittedGames.indexOf(game.Id) >= 0}">
                            {{game.UrlStub}}
                        </span>
                    </div>
                </div>
            </template>
            <div  v-if="!loadedFull" class="p-4 has-text-centered">
                <button class="button is-primary" @click="loadFullList()">Load More Runners</button>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { GameStore } from "@/store/games";
import { Game, StaticContentService, UserList, UserListEntry } from "@/api";
import { Component, Vue } from "vue-facing-decorator";
import { ContentLoader } from "vue-content-loader";
import CountryFlag from "vue-country-flag-next";

@Component({
    components: {
        ContentLoader,
        CountryFlag
    }
})
export default class RunnerListGrid extends Vue {
    runnerListClean: UserList | null = null;
    runnerList: readonly UserListEntry[] | null = null;
    games: Game[] = new Array();
    loadedFull = false;

    gameFilter: string | null = null;
    search: string | null = null;

    async mounted() {
        let userListPromise = StaticContentService.userListTop100();
        let allGames = (await GameStore.get()).games;
        this.games = allGames.filter(g => !g.Hidden && (g.RunnableSegments?.filter(r => r.Points && r.Points > 0)?.length || 0) > 0)
        
        this.runnerListClean = await userListPromise;
        this.processList();
    }

    async loadFullList() {
        if(this.loadedFull) return;

        this.loadedFull = true;
        this.runnerListClean = await StaticContentService.userList();
        this.processList();
    }

    async filter(game: Game) {
        await this.loadFullList();

        if(game.Id == null) return;

        if(this.gameFilter != game.Id) {
            this.gameFilter = game.Id;
        } else {
            this.gameFilter = null;
        }

        this.processList();
    }

    searchTimeout : number | null = null;
    async applySearch() {
        await this.loadFullList();

        if(this.searchTimeout != null) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => 
        {
            this.searchTimeout = null;
            this.processList();
        }, 750);
    }

    processList() {
        if(this.runnerListClean?.Entries == null)
            return;

        let runnerListCopy: UserListEntry[] = new Array();

        var searchPattern = new RegExp(".*" + this.search + ".*", "i");

        var index = 0;
        for(var entry of this.runnerListClean.Entries) {
            (entry as any).rank = ++index;

            let exclude = false;

            if(entry.SubmittedGames?.length == 0)
                exclude = true;

            if(this.search) {
                if(!searchPattern.test(entry.Username!))
                    exclude = true;
            }

            if(this.gameFilter != null) {
                var filtered = entry.SubmittedGames?.indexOf(this.gameFilter, 0) ?? -1;

                if(filtered == -1)
                        exclude = true;
            }

            if(!exclude)
            {
                runnerListCopy.push(entry);
            }
        }

        let ptAccessor = (a: UserListEntry) => a.Points ?? 0;

        if(this.gameFilter != null) {
            ptAccessor = (a: UserListEntry) => (a?.PointsPerGame![this.gameFilter!]) ?? 0;
        }

        this.runnerList = Object.freeze(runnerListCopy.sort((a,b) => ptAccessor(b) - ptAccessor(a)));
    }
}

</script>
<style lang="scss" scoped>
@use "@/assets/haloruns_vars.scss" as *;

.runner-table {
  @include hr-tile-full-bleed;

  .ranks {
    text-align: left;
    padding-left: 25px;

    .rank {
      font-size: 1.0rem;
      font-family: "Oswald", sans-serif;
    }
  }

  .game-header {
      width: 0%;
      text-align: center;
  }

  .game-symbol {
      text-align: center;
  }

  td:last-child {
    padding-right: 25px;
  }
}

.social-link {
    margin-left: 10px;
    position: relative;
    top: 2px;

    img {
        height: 16px;
        width: 16px;
        position: relative;
    }

    &.twitch {
        &:hover {
            img {
                filter: brightness(0.5) sepia(1) saturate(10000%)  brightness(2) hue-rotate(270deg);
            }
        }
    }

    &.youtube {
        &:hover {
            img {
                filter: brightness(0.5) sepia(1) saturate(10000%)  brightness(2) hue-rotate(25deg);
            }
        }
    }
}

.runner-cards {
    @include hr-tile-full-bleed;
    @include hr-tile-full-bleed-vertical;

    border-bottom: 2px solid $border-color;

    .runner-card {
        padding-left: 25px;
        padding-right: 25px;
        padding-top: 7px;
        padding-bottom: 7px;
        
        &:not(:last-child) {
            border-bottom: 1px solid $border-color;
        }

        .runner-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 1rem;

            .headline {
                display: flex;
            }
        }

        .game-symbols {
            display: flex;
            justify-content: space-between;
            align-content: center;
            flex-wrap: wrap;

            .game-symbol {
                margin: 0 0.25rem;
                text-transform: uppercase;
                font-weight: bold;
                color: #fff;
                opacity: 0.25;

                &:first-child {
                    margin-left: 0;
                }

                &.submitted {
                    opacity: 1;
                }
            }
        }
    }

}

.darker {
    opacity: 0.25;
}

.sort-button {
    cursor: pointer;
    &:hover, &:focus {
        color: $primary;
    }

    &.ascending {
        &::after {
            content: '△';
        }
    }

    &.descending {
        &::after {
            content: '▽';
        }
    }
}

.filter-button {
    cursor: pointer;
    &:hover, &:focus {
        color: $primary;
    }

    &.filterExclude {
        &::after {
            content: '☒';
            color: $warning-color;
        }
    }

    &.filterRequire {
        &::after {
            
            content: '☑';
            color: $success-color;
        }
    }
}
</style>